import { stickerEmoji, type Generation, type GenerationResult } from '@trendator/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Back10Icon, BackIcon, DownloadIcon, LinkIcon, LyricsIcon, PauseIcon, PlayFill, Plus, RepeatIcon, ShareIcon, SoundIcon, TickIcon } from '../components/icons';
import { plural } from '../format';
import { canShareToStory, copyText, downloadFile, haptic, openTelegram, shareToStory } from '../telegram';

const FORMAT_LABEL: Record<string, string> = { '9:16': 'Сторис', '1:1': 'Пост', '16:9': 'YouTube', '4:5': 'Лента' };

export function clock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Плашка «Проверено»: сколько кадров прошло контроль и сколько переделали за наш счёт. */
export function qcText(gen: Generation): string | null {
  if (!gen.qc || gen.qc.checked === 0) return null;
  const re = gen.qc.regenerated > 0 ? ` · переделали ${gen.qc.regenerated} бесплатно` : '';
  return `Проверено ${gen.qc.checked} из ${gen.qc.checked}${re}`;
}

/** Готовое видео — docs/design/VideoResult.dc.html. */
export function VideoDone({ gen, took, onBack, onRepeat, footer }: { gen: Generation; took: string; onBack: () => void; onRepeat: () => void; footer?: ReactNode }) {
  const [sel, setSel] = useState(0);
  const result = gen.results[sel] ?? gen.results[0]!;
  const formats = [{ aspect: (gen.aspectRatio ?? '9:16') as string, url: result.url }, ...(result.formats ?? [])];
  const [fmt, setFmt] = useState(0);
  const src = formats[fmt]?.url ?? result.url;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [time, setTime] = useState({ cur: 0, dur: result.durationSec ?? 0 });

  useEffect(() => {
    setFmt(0);
  }, [sel]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    haptic();
    if (v.paused) void v.play().catch(() => {});
    else v.pause();
  };

  const seek = (e: React.PointerEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !time.dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    v.currentTime = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) * time.dur;
  };

  const pct = time.dur ? (time.cur / time.dur) * 100 : 0;
  const qc = qcText(gen);

  return (
    <main className="screen vres">
      <div className="vres__stage">
        <video
          key={src}
          ref={videoRef}
          className="vres__video"
          src={src}
          poster={result.coverUrl ?? undefined}
          playsInline
          autoPlay
          loop
          muted={muted}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => {
          const { currentTime, duration } = e.currentTarget;
          setTime((t) => ({ cur: currentTime, dur: Number.isFinite(duration) && duration > 0 ? duration : t.dur }));
        }}
          onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          setTime((t) => ({ ...t, dur: Number.isFinite(d) && d > 0 ? d : t.dur }));
        }}
          onClick={toggle}
        />
        <div className="vres__top">
          <button type="button" className="vres__glass" aria-label="Назад" onClick={onBack}>
            <BackIcon />
          </button>
          <span className="vres__badge">
            {qc ? (
              <>
                <span className="vres__tick">
                  <TickIcon />
                </span>
                Проверено · {took}
              </>
            ) : (
              took
            )}
          </span>
          <button
            type="button"
            className="vres__glass"
            aria-label={muted ? 'Включить звук' : 'Выключить звук'}
            onClick={() => {
              haptic('select');
              setMuted((m) => !m);
            }}
          >
            <SoundIcon muted={muted} />
          </button>
        </div>
        <button type="button" className={`vres__play${playing ? ' vres__play--hide' : ''}`} aria-label={playing ? 'Пауза' : 'Играть'} onClick={toggle}>
          {playing ? <PauseIcon /> : <PlayFill />}
        </button>
        <div className="vres__bottom">
          <span className="vres__title">{gen.title}</span>
          <div className="vres__time">
            <span>{clock(time.cur)}</span>
            <div className="vres__track" onPointerDown={seek} role="slider" aria-label="Перемотка" aria-valuemin={0} aria-valuemax={Math.round(time.dur)} aria-valuenow={Math.round(time.cur)}>
              <span className="vres__fill" style={{ width: `${pct}%` }} />
              <span className="vres__knob" style={{ left: `${pct}%` }} />
            </div>
            <span>{clock(time.dur)}</span>
          </div>
        </div>
      </div>

      {gen.results.length > 1 ? (
        <div className="segments vres__variants" role="group" aria-label="Вариант">
          {gen.results.map((r, i) => (
            <button
              key={r.url}
              type="button"
              aria-pressed={i === sel}
              onClick={() => {
                haptic('select');
                setSel(i);
              }}
            >
              {r.label || `Вариант ${i + 1}`}
            </button>
          ))}
        </div>
      ) : null}

      {formats.length > 1 ? (
        <div className="vres__formats">
          <div className="vres__formats-head">
            <span>Собрали под площадки</span>
            <span>{result.coverUrl ? 'с обложкой' : ''}</span>
          </div>
          <div className="segments vres__segments">
            {formats.map((f, i) => (
              <button
                key={f.aspect}
                type="button"
                aria-pressed={i === fmt}
                onClick={() => {
                  haptic('select');
                  setFmt(i);
                }}
              >
                {f.aspect}
                {FORMAT_LABEL[f.aspect] ? ` · ${FORMAT_LABEL[f.aspect]}` : ''}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {qc && gen.qc && gen.qc.regenerated > 0 ? <p className="done__hint">{qc}</p> : null}

      <div className="vres__actions">
        <button
          type="button"
          className="btn-accent"
          onClick={() => {
            haptic();
            downloadFile(src, `trendator-${gen.id.slice(0, 8)}-${(formats[fmt]?.aspect ?? '').replace(':', 'x')}.mp4`);
          }}
        >
          <DownloadIcon />
          Скачать
        </button>
        {canShareToStory() ? (
          <button
            type="button"
            className="vres__round"
            aria-label="В сторис"
            onClick={() => {
              haptic();
              shareToStory(formats[0]!.url);
            }}
          >
            <ShareIcon />
          </button>
        ) : null}
        <button type="button" className="vres__round" aria-label="Повторить" onClick={onRepeat}>
          <RepeatIcon />
        </button>
      </div>
      {footer}
    </main>
  );
}

/** Декоративная волна: стабильные высоты по индексу. */
const BARS = Array.from({ length: 28 }, (_, i) => 25 + Math.round(Math.abs(Math.sin(i * 1.7) * 0.6 + Math.sin(i * 0.45) * 0.4) * 75));

/** Готовая песня — docs/design/Song.dc.html. */
export function SongDone({ gen, onBack, onRepeat, footer }: { gen: Generation; onBack: () => void; onRepeat: () => void; footer?: ReactNode }) {
  const [sel, setSel] = useState(0);
  const track: GenerationResult = gen.results[sel] ?? gen.results[0]!;
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState({ cur: 0, dur: track.durationSec ?? 0 });
  const [lyrics, setLyrics] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setTime({ cur: 0, dur: track.durationSec ?? 0 });
  }, [track.url, track.durationSec]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    haptic();
    if (a.paused) void a.play().catch(() => {});
    else a.pause();
  };
  const back10 = () => {
    const a = audioRef.current;
    if (!a) return;
    haptic('select');
    a.currentTime = Math.max(0, a.currentTime - 10);
  };
  const seek = (e: React.PointerEvent<HTMLDivElement>) => {
    const a = audioRef.current;
    if (!a || !time.dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) * time.dur;
  };

  const pct = time.dur ? time.cur / time.dur : 0;
  const title = track.title || gen.title;
  const meta = [track.label, time.dur ? clock(time.dur) : null].filter(Boolean).join(' · ');

  return (
    <main className="screen song">
      <header className="song__header">
        <button type="button" className="glass icon-btn" aria-label="Назад" onClick={onBack}>
          <BackIcon />
        </button>
        <span className="song__head-title">{gen.title}</span>
        <span className="song__spacer" />
      </header>

      <div className="song__cover" style={track.coverUrl ? { backgroundImage: `url("${track.coverUrl}")` } : undefined}>
        {lyrics && track.lyrics ? <pre className="song__lyrics">{track.lyrics}</pre> : null}
        {!track.coverUrl && !lyrics ? <span className="song__cover-text">{title}</span> : null}
      </div>

      <div className="song__info">
        <span className="song__title">{title}</span>
        {meta ? <span className="song__meta">{meta}</span> : null}
      </div>

      <audio
        key={track.url}
        ref={audioRef}
        src={track.url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => {
          const { currentTime, duration } = e.currentTarget;
          setTime((t) => ({ cur: currentTime, dur: Number.isFinite(duration) && duration > 0 ? duration : t.dur }));
        }}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          setTime((t) => ({ ...t, dur: Number.isFinite(d) && d > 0 ? d : t.dur }));
        }}
      />

      <div className="song__wave" onPointerDown={seek} role="slider" aria-label="Перемотка" aria-valuemin={0} aria-valuemax={Math.round(time.dur)} aria-valuenow={Math.round(time.cur)}>
        {BARS.map((h, i) => (
          <span key={i} className={i / BARS.length < pct ? 'on' : ''} style={{ height: `${h}%` }} />
        ))}
      </div>
      <div className="song__times">
        <span>{clock(time.cur)}</span>
        <span>{clock(time.dur)}</span>
      </div>

      <div className="song__controls">
        <button type="button" className="song__ctl" aria-label="Назад 10 секунд" onClick={back10}>
          <Back10Icon />
        </button>
        <button type="button" className="song__play" aria-label={playing ? 'Пауза' : 'Играть'} onClick={toggle}>
          {playing ? <PauseIcon /> : <PlayFill />}
        </button>
        <button
          type="button"
          className={`song__ctl${lyrics ? ' song__ctl--on' : ''}`}
          aria-label="Слова"
          aria-pressed={lyrics}
          disabled={!track.lyrics}
          onClick={() => {
            haptic('select');
            setLyrics((l) => !l);
          }}
        >
          <LyricsIcon />
        </button>
      </div>

      {gen.results.length > 1 ? (
        <div className="segments song__tracks" role="group" aria-label="Вариант">
          {gen.results.map((r, i) => (
            <button
              key={r.url}
              type="button"
              aria-pressed={i === sel}
              onClick={() => {
                haptic('select');
                audioRef.current?.pause();
                setSel(i);
              }}
            >
              Вариант {i + 1}
            </button>
          ))}
        </div>
      ) : null}

      <div className="song__actions">
        <button
          type="button"
          className="btn-glass"
          onClick={() => {
            haptic();
            downloadFile(track.url, `trendator-${gen.id.slice(0, 8)}-${sel + 1}.mp3`);
          }}
        >
          <DownloadIcon />
          Скачать
        </button>
        <button type="button" className="btn-accent" onClick={onRepeat}>
          <RepeatIcon />
          Ещё раз
        </button>
      </div>
      <p className="done__hint">Песня уже в чате с ботом — можно сразу переслать.</p>
      {footer}
    </main>
  );
}

/** Стикерпак готов — docs/design/Stickers.dc.html. */
export function StickersDone({ gen, onBack, onRepeat, footer }: { gen: Generation; onBack: () => void; onRepeat: () => void; footer?: ReactNode }) {
  const [copied, setCopied] = useState(false);
  const url = gen.stickers?.url ?? null;
  const shown = gen.results.slice(0, 15);
  const more = gen.results.length - shown.length;
  return (
    <main className="screen stickers">
      <header className="stickers__header">
        <button type="button" className="glass icon-btn" aria-label="Назад" onClick={onBack}>
          <BackIcon />
        </button>
        <div className="stickers__head">
          <span className="stickers__title">{url ? 'Стикерпак готов' : 'Стикеры готовы'}</span>
          <span className="stickers__sub">
            {gen.results.length} {plural(gen.results.length, 'эмоция', 'эмоции', 'эмоций')} с твоим лицом
          </span>
        </div>
      </header>

      <div className="glass-list stickers__grid">
        {shown.map((r, i) => (
          <div key={r.url} className="stickers__item">
            <img src={r.url} alt={r.label || `Стикер ${i + 1}`} />
            <span>{stickerEmoji(r.label)}</span>
          </div>
        ))}
        {more > 0 ? <div className="stickers__more">+{more}</div> : null}
      </div>

      {url ? (
        <button
          type="button"
          className="glass-list stickers__link"
          onClick={async () => {
            if (await copyText(url)) {
              haptic('select');
              setCopied(true);
              setTimeout(() => setCopied(false), 1800);
            }
          }}
        >
          <LinkIcon />
          <span className="stickers__url">{url.replace('https://', '')}</span>
          <span className="stickers__copy">{copied ? 'Готово' : 'Копировать'}</span>
        </button>
      ) : (
        <p className="stickers__note">Пак в Telegram не собрался — стикеры пришли картинками в бот. Напиши в поддержку, соберём вручную.</p>
      )}
      {url ? <p className="stickers__note">Пак твой: можно дарить друзьям ссылкой</p> : null}

      <div className="result__footer">
        {url ? (
          <button
            type="button"
            className="btn-accent"
            onClick={() => {
              haptic();
              openTelegram(url);
            }}
          >
            <Plus />
            Добавить в Telegram
          </button>
        ) : null}
        <button type="button" className="btn-glass" onClick={onRepeat}>
          Сделать ещё
        </button>
      </div>
      {footer}
    </main>
  );
}
